/**
 * Returns all focusable elements within a container.
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
const getFocusableElements = (container) =>
  Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), details > summary',
    ),
  ).filter((el) => !el.closest('[inert]') && !el.closest('[hidden]'));

/**
 * Sets `inert` on all siblings of the modal and siblings of its ancestors up to <body>.
 * @param {HTMLElement} excludeEl — the element to keep interactive
 * @param {HTMLElement|null} triggerElement — the element that triggered the open (for focus return on close)
 * @param {HTMLElement[]} keepInteractive — extra elements to leave interactive. A
 *   drawer that opens below the header rather than over it needs this: the modal
 *   lives inside the header, so walking up would otherwise inert the very
 *   controls that stay visible next to it.
 */
const setPageInert = (excludeEl, triggerElement, keepInteractive = []) => {
  let current = excludeEl;
  while (current && current !== document.body) {
    const parent = current.parentElement;
    if (!parent) break;
    Array.from(parent.children).forEach((sibling) => {
      if (
        sibling !== current &&
        sibling !== triggerElement &&
        !keepInteractive.some((el) => el && (el === sibling || sibling.contains(el))) &&
        !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName) &&
        !sibling.hasAttribute('inert')
      ) {
        sibling.setAttribute('inert', '');
        sibling.setAttribute('data-a11y-inert', 'true');
      }
    });
    current = parent;
  }
};

/**
 * Removes `inert` from all elements that were dynamically inerted.
 */
const removePageInert = () => {
  document.querySelectorAll('[data-a11y-inert]').forEach((el) => {
    el.removeAttribute('inert');
    el.removeAttribute('data-a11y-inert');
  });
};
/**
 * Opens a modal/drawer.
 * @param {HTMLElement|string} modal — the modal element or a CSS selector
 * @param {HTMLElement|null} triggerElement — the element that triggered the open (for focus return on close)
 */
const getTransitionDuration = (el) => {
  const raw = getComputedStyle(el).transitionDuration.split(',')[0].trim();
  return raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
};

const openModal = (modal, triggerElement = null, { keepInteractive = [] } = {}) => {
  if (!modal) return;
  const foundModal = typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  foundModal._a11yTrigger =
    triggerElement ?? (document.activeElement !== document.body ? document.activeElement : null);
  // Kept for closeModal, which marks the page again for this modal when one
  // stacked over it goes away
  foundModal._a11yKeepInteractive = keepInteractive;

  document.documentElement.style.overflow = 'hidden';

  window.requestAnimationFrame(() => {
    foundModal.classList.add('open');
    // Drop whatever a previous modal marked before marking our own. Handing over
    // from one to another — the mobile drawer to the minicart, say — would
    // otherwise leave the incoming modal sitting under the outgoing one's inert
    // ancestor, since setPageInert skips anything already flagged.
    removePageInert();
    setPageInert(foundModal, triggerElement, keepInteractive);

    const focusable = getFocusableElements(foundModal);
    if (focusable.length > 0) focusable[0].focus();
    else if (foundModal.getAttribute('tabindex')) foundModal.focus();

    foundModal._a11yEscHandler = (e) => {
      if (e.key === 'Escape') closeModal(foundModal);
    };

    // Focus trap: cycle Tab/Shift+Tab within the modal
    foundModal._a11yTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements(foundModal);
      if (!focusable.length) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', foundModal._a11yEscHandler);
    document.addEventListener('keydown', foundModal._a11yTrapHandler);
  });
};

/**
 * Closes a modal/drawer.
 * @param {HTMLElement|string} modal — the modal element or a CSS selector
 * @param {boolean} restoreFocus — whether to send focus back to whatever opened
 *   it. Pass false when closing because another surface is taking focus, so the
 *   two do not fight over it once the transition ends.
 */
const closeModal = (modal, { restoreFocus = true } = {}) => {
  if (!modal) return;
  const foundModal = typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  if (foundModal._a11yEscHandler) {
    document.removeEventListener('keydown', foundModal._a11yEscHandler);
    foundModal._a11yEscHandler = null;
  }

  if (foundModal._a11yTrapHandler) {
    document.removeEventListener('keydown', foundModal._a11yTrapHandler);
    foundModal._a11yTrapHandler = null;
  }

  const duration = getTransitionDuration(foundModal);
  window.requestAnimationFrame(() => {
    foundModal.classList.remove('open');
    setTimeout(() => {
      // Another modal can still be open: one that opened while this one animated
      // out (the minicart taking over from the mobile drawer), or one this modal
      // was stacked over (the language drawer opened from the mobile menu). It
      // keeps the scroll lock, and the inert flags go back to being its own:
      // the ones left by this modal also cover the modal underneath, which
      // would leave nothing clickable.
      const stillOpen = Array.from(document.querySelectorAll('.modal.open')).pop();
      removePageInert();
      if (stillOpen) {
        setPageInert(stillOpen, stillOpen._a11yTrigger, stillOpen._a11yKeepInteractive);
      } else {
        document.documentElement.style.overflow = '';
      }
      if (restoreFocus) foundModal._a11yTrigger?.focus();
      foundModal._a11yTrigger = null;
    }, duration);
  });
};

const initModals = (root = document) => {
  if (!root) return;
  const rootEl = typeof root === 'string' ? document.querySelector(root) : root;
  if (!rootEl) return;

  rootEl.querySelectorAll('.modal').forEach((modal) => {
    modal.querySelectorAll('.modal__backdrop').forEach((el) => el.addEventListener('click', () => closeModal(modal)));

    modal
      .querySelectorAll('.modal__close, .modal__refuse')
      .forEach((el) => el.addEventListener('click', () => closeModal(modal)));

    if (modal.id) {
      // Anchors pointing at the modal id, plus an explicit trigger declared by
      // the modal itself: a <button> has no href to match on.
      const selectors = [`a[href*="${modal.id}"]`];
      if (modal.dataset.triggerId) selectors.push(`#${modal.dataset.triggerId}`);

      rootEl.querySelectorAll(selectors.join(', ')).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          openModal(modal, btn);
        }),
      );
    }
  });
};

export { initModals, openModal, closeModal, getFocusableElements };
